/*
 * <shop-by-need> — tab switching for sections/shop-by-need.liquid.
 *
 * Every panel is already in the DOM; this only moves `hidden`,
 * `aria-selected` and the roving tabindex. Tabs, panels and "see all" links
 * are joined by `data-need-index`, so the link above the row always matches
 * the row. Keyboard follows the WAI-ARIA tabs pattern with automatic
 * activation: arrows, Home and End move and select at once.
 *
 * In the Theme Editor, selecting a block opens its tab.
 */
if (!customElements.get('shop-by-need')) {
  customElements.define(
    'shop-by-need',
    class ShopByNeed extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.links = Array.from(this.querySelectorAll('.shop-by-need__view-all'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab, false));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });

        this.onBlockSelect = (event) => {
          // The tab carries the block's shopify_attributes, so it IS the target.
          const tab = this.tabs.find((t) => t === event.target);
          if (tab) this.select(tab, false);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      onKeydown(event) {
        const current = this.tabs.indexOf(event.currentTarget);
        const last = this.tabs.length - 1;
        let next;

        switch (event.key) {
          case 'ArrowRight':
            next = current === last ? 0 : current + 1;
            break;
          case 'ArrowLeft':
            next = current === 0 ? last : current - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(this.tabs[next], true);
      }

      select(tab, focus) {
        const index = tab.dataset.needIndex;
        if (tab.getAttribute('aria-selected') === 'true') {
          if (focus) tab.focus();
          return;
        }

        this.tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const open = panel.dataset.needIndex === index;
          panel.hidden = !open;
          panel.classList.toggle('is-entering', open);
        });

        this.links.forEach((link) => {
          link.hidden = link.dataset.needIndex !== index;
        });

        if (focus) tab.focus({ preventScroll: true });
        this.revealTab(tab);
      }

      // Keep the selected pill in view on a phone, where the row scrolls.
      // Not scrollIntoView: that scrolls every ancestor, and the closed cart
      // drawer parked off-screen to the right lets it slide the whole page.
      revealTab(tab) {
        const row = tab.parentElement;
        if (row.scrollWidth <= row.clientWidth) return;
        const left = tab.offsetLeft - row.offsetLeft;
        const right = left + tab.offsetWidth;
        if (left < row.scrollLeft) {
          row.scrollTo({ left: left - 24, behavior: 'smooth' });
        } else if (right > row.scrollLeft + row.clientWidth) {
          row.scrollTo({ left: right - row.clientWidth + 24, behavior: 'smooth' });
        }
      }
    }
  );
}
